import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, Typography, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, CircularProgress, Button, 
  Card, CardContent, Chip, List, ListItem, ListItemText,
  ToggleButtonGroup, ToggleButton, TextField, Grid, Divider,
  Skeleton, LinearProgress 
} from '@mui/material';
import testService from '../services/testService';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import confetti from 'canvas-confetti';
import { initTawk, hideTawk, updateTawkAttributes } from '../services/tawk';
import 'katex/dist/katex.min.css';
import { InlineMath } from 'react-katex';

const API_IMAGE_BASE = process.env.REACT_APP_API_URL || ''; // Base URL for image paths

const TestDetailedResults = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [questionsMeta, setQuestionsMeta] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [testName, setTestName] = useState('');
  const [sessionQuestions, setSessionQuestions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [celebrateCooldown, setCelebrateCooldown] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(true);
  const [filterMode, setFilterMode] = useState('all');
  const [jumpToQuestion, setJumpToQuestion] = useState('');
  const formatAbs = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString(); } catch { return ''; }
  };

  const handleCelebrate = () => {
    if (celebrateCooldown) return;
    setCelebrateCooldown(true);
    // Big burst from center
    confetti({ particleCount: 400, spread: 120, startVelocity: 50, origin: { x: 0.5, y: 0.6 } });
    // Burst from left edge
    confetti({ particleCount: 300, spread: 100, startVelocity: 40, origin: { x: 0.1, y: 0.6 } });
    // Burst from right edge
    confetti({ particleCount: 300, spread: 100, startVelocity: 40, origin: { x: 0.9, y: 0.6 } });
    setTimeout(() => setCelebrateCooldown(false), 10000);
  };

  const handleOpen = (question) => {
    setSelectedQuestion(question);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedQuestion(null);
  };

  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        const resultData = await testService.getMyTestResults(testId);
        const testInfo = await testService.getTestById(testId);
        const questionsList = await testService.getQuestionsByTest(testId);
        setTestName(testInfo.name);
        setSessionQuestions(questionsList);
        setQuestionsMeta(resultData.Questions || resultData.questions || []);
        setResults(resultData.Results || resultData.results || []);
      } catch (err) {
        setError('Failed to load detailed results.');
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [testId]);

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        setFeedbackLoading(true);
        const fb = await testService.getMyFeedback(testId);
        setFeedback(fb);
        // Auto-mark read on page visit (server already marks, this just ensures client-side and future UX)
        if (fb && (fb.feedbackId || fb.FeedbackId)) {
          const id = fb.feedbackId ?? fb.FeedbackId;
          try { await testService.markNotificationRead(id); } catch {}
        }
      } catch (e) {
        setFeedback({ notViewedByTeacherYet: true });
      } finally {
        setFeedbackLoading(false);
      }
    };
    fetchFeedback();
  }, [testId]);

  // Mount Tawk chat on results page
  useEffect(() => {
    const studentPk = localStorage.getItem('student_pk') || localStorage.getItem('student_id') || '';
    const studentName = localStorage.getItem('student_name') || '';
    initTawk({
      visitor: studentName ? { name: studentName } : (studentPk ? { name: `Student ${studentPk}` } : undefined),
      attributes: { page: 'results', testId: testId || '' },
    });
    updateTawkAttributes({ page: 'results', testId: testId || '' });
    return () => hideTawk();
  }, [testId]);

  const studentPk = parseInt(localStorage.getItem('student_pk') || localStorage.getItem('student_id'), 10);
  const myResult = results.find(r => r.StudentId === studentPk || r.studentId === studentPk);

  // Filter questions based on mode
  const filteredDetails = myResult?.details.filter(d => {
    if (filterMode === 'correct') return d.IsCorrect ?? d.isCorrect;
    if (filterMode === 'incorrect') return !(d.IsCorrect ?? d.isCorrect);
    if (filterMode === 'verbal') return (d.QuestionType ?? d.questionType).toLowerCase() === 'verbal';
    if (filterMode === 'math') return (d.QuestionType ?? d.questionType).toLowerCase() === 'math';
    return true;
  }) || [];

  // Calculate summary statistics
  const stats = {
    totalQuestions: myResult?.details.length || 0,
    correctCount: myResult?.CorrectCount ?? myResult?.correctCount ?? 0,
    incorrectCount: myResult?.IncorrectCount ?? myResult?.incorrectCount ?? 0,
    accuracyPercent: myResult ? Math.round(((myResult.CorrectCount ?? myResult.correctCount) / myResult.details.length) * 100) : 0
  };

  const handleJumpToQuestion = () => {
    const qNum = parseInt(jumpToQuestion, 10);
    if (qNum && qNum > 0 && qNum <= (myResult?.details.length || 0)) {
      const elem = document.getElementById(`question-${qNum}`);
      if (elem) elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const isMathQuestion = (q) => ((q?.questionType || '').toLowerCase() === 'math');
  const hasOptions = (q) => !!(q?.optionA || q?.optionB || q?.optionC || q?.optionD);
  const renderInline = (content, isMath) => isMath ? <InlineMath math={String(content || '')} /> : <>{content}</>;
  const toHtml = (text) => {
    if (!text) return '';
    const str = String(text);
    return /<[^>]+>/.test(str) ? str : str.replace(/\n/g, '<br/>');
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress sx={{ mb: 3 }} />
        <Skeleton variant="text" width="60%" height={50} sx={{ mb: 2 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map(i => (
            <Grid item xs={6} sm={3} key={i}>
              <Skeleton variant="rectangular" height={100} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }
  if (error) {
    return <Typography color="error">{error}</Typography>;
  }
  if (!myResult) {
    return <Typography>No results found for this test.</Typography>;
  }

  return (<>
    
    <Box sx={{ p: 2 }}>

      <Typography variant="h4" gutterBottom>{`Detailed Results: ${testName}`}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" gutterBottom>{myResult.StudentName || myResult.studentName || 'Your Results'}</Typography>
        <Button variant="outlined" size="small" onClick={() => navigate(`/leaderboard/${testId}`)}>
          View Leaderboard
        </Button>
      </Box>

      {/* Summary Statistics */}
      <Grid container spacing={2} sx={{ my: 2 }}>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="primary.main">{stats.totalQuestions}</Typography>
              <Typography variant="caption" color="text.secondary">Total Questions</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="success.main">{stats.correctCount}</Typography>
              <Typography variant="caption" color="text.secondary">Correct</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="error.main">{stats.incorrectCount}</Typography>
              <Typography variant="caption" color="text.secondary">Incorrect</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" color="info.main">{stats.accuracyPercent}%</Typography>
              <Typography variant="caption" color="text.secondary">Accuracy</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Teacher Feedback */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Teacher Feedback</Typography>
          {feedbackLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading feedback…</Typography>
            </Box>
          ) : feedback && feedback.approved ? (
            <>
              <Typography variant="caption" color="text.secondary">
                Released: {formatAbs(feedback.approvedAt || feedback.ApprovedAt)}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                {feedback.content || ''}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">Not viewed by the teacher yet</Typography>
          )}
        </CardContent>
      </Card>

      
      {/* Filter Controls */}
      <Box sx={{ my: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ mr: 1 }}>Filter:</Typography>
          <ToggleButtonGroup
            value={filterMode}
            exclusive
            onChange={(e, val) => val && setFilterMode(val)}
            size="small"
          >
            <ToggleButton value="all">All ({myResult?.details.length || 0})</ToggleButton>
            <ToggleButton value="correct">Correct ({stats.correctCount})</ToggleButton>
            <ToggleButton value="incorrect">Incorrect ({stats.incorrectCount})</ToggleButton>
            <ToggleButton value="verbal">Verbal</ToggleButton>
            <ToggleButton value="math">Math</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Jump to #"
            type="number"
            value={jumpToQuestion}
            onChange={(e) => setJumpToQuestion(e.target.value)}
            sx={{ width: 100 }}
            InputProps={{ inputProps: { min: 1, max: myResult?.details.length || 0 } }}
          />
          <Button variant="outlined" size="small" onClick={handleJumpToQuestion}>Go</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCelebrate}
            disabled={celebrateCooldown}
            size="small"
          >
            Celebrate 🎉
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing {filteredDetails.length} of {myResult?.details.length || 0} questions
      </Typography>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Question</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Your Answer</TableCell>
              <TableCell>Correct Answer</TableCell>
              <TableCell>Correct?</TableCell>
                <TableCell>View</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDetails.map((d, idx) => {
              const q = sessionQuestions.find(q => q.questionNumber === (d.QuestionNumber ?? d.questionNumber) && q.questionType.toLowerCase() === (d.QuestionType ?? d.questionType).toLowerCase());
              const math = isMathQuestion(q);
              const openResponse = q ? !hasOptions(q) : false;
              const correctAnswerValue = openResponse ? (q?.correctAnswerText ?? q?.correctAnswer) : (q?.correctAnswer);
              const userAnswerValue = d.Answer ?? d.answer;
              const questionNum = d.QuestionNumber ?? d.questionNumber;
              return (
                <TableRow key={idx} id={`question-${questionNum}`}>
                  <TableCell>{questionNum}</TableCell>
                  <TableCell>{d.QuestionType ?? d.questionType}</TableCell>
                  <TableCell>{renderInline(userAnswerValue, math)}</TableCell>
                  <TableCell>{renderInline(correctAnswerValue, math)}</TableCell>
                  <TableCell>{(d.IsCorrect ?? d.isCorrect) ? '✅' : '❌'}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpen(q)}>
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 2 }}>
        <Button variant="contained" onClick={() => navigate('/')}>Return to Homepage</Button>
      </Box>
    </Box>
    <Dialog open={openDialog} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Question Details</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ p: 1 }}>
          {/* Header Chips */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Chip label={selectedQuestion?.questionType} color={selectedQuestion?.questionType === 'Verbal' ? 'primary' : 'secondary'} />
            <Chip label={`${selectedQuestion?.points} Points`} color="success" />
          </Box>
          {/* Passage */}
          {(selectedQuestion?.passageText || selectedQuestion?.PassageText) && (
            <Card sx={{ mb: 2, bgcolor: '#f8f9fa' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Passage</Typography>
                <Box sx={{ whiteSpace: 'normal' }}>
                  <div dangerouslySetInnerHTML={{ __html: toHtml(selectedQuestion.passageText ?? selectedQuestion.PassageText) }} />
                </Box>
              </CardContent>
            </Card>
          )}
          {/* Question */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Question</Typography>
              {isMathQuestion(selectedQuestion) ? (
                <InlineMath math={String(selectedQuestion?.text || '')} />
              ) : (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedQuestion?.text}
                </Typography>
              )}
            </CardContent>
          </Card>
          {/* Image */}
          {selectedQuestion?.imageUrl && (
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <img src={`${API_IMAGE_BASE}${selectedQuestion.imageUrl}`} alt="Question Image" style={{ maxWidth: '100%', maxHeight: '300px' }} />
            </Box>
          )}
          {/* Answer Choices / Open-Response */}
          {(() => {
            const openResponse = selectedQuestion ? !hasOptions(selectedQuestion) : false;
            const math = isMathQuestion(selectedQuestion);
            if (openResponse) {
              const ans = selectedQuestion?.correctAnswerText ?? selectedQuestion?.correctAnswer;
              return (
                <Box>
                  <Typography variant="h6" gutterBottom>Correct Answer</Typography>
                  <Typography variant="body1">
                    {renderInline(ans, math)}
                  </Typography>
                </Box>
              );
            }
            return (
              <>
                <Typography variant="h6" gutterBottom>Answer Choices</Typography>
                <List>
                  {/* Option A */}
                  <ListItem sx={{ bgcolor: selectedQuestion?.correctAnswer === 'A' ? '#e8f5e9' : 'transparent', borderLeft: selectedQuestion?.correctAnswer === 'A' ? '4px solid #4caf50' : 'none' }}>
                    <ListItemText primary={<Typography fontWeight={selectedQuestion?.correctAnswer === 'A' ? 'bold' : 'normal'}>A. {renderInline(selectedQuestion?.optionA, math)}</Typography>} />
                  </ListItem>
                  {/* Option B */}
                  <ListItem sx={{ bgcolor: selectedQuestion?.correctAnswer === 'B' ? '#e8f5e9' : 'transparent', borderLeft: selectedQuestion?.correctAnswer === 'B' ? '4px solid #4caf50' : 'none' }}>
                    <ListItemText primary={<Typography fontWeight={selectedQuestion?.correctAnswer === 'B' ? 'bold' : 'normal'}>B. {renderInline(selectedQuestion?.optionB, math)}</Typography>} />
                  </ListItem>
                  {/* Option C */}
                  <ListItem sx={{ bgcolor: selectedQuestion?.correctAnswer === 'C' ? '#e8f5e9' : 'transparent', borderLeft: selectedQuestion?.correctAnswer === 'C' ? '4px solid #4caf50' : 'none' }}>
                    <ListItemText primary={<Typography fontWeight={selectedQuestion?.correctAnswer === 'C' ? 'bold' : 'normal'}>C. {renderInline(selectedQuestion?.optionC, math)}</Typography>} />
                  </ListItem>
                  {/* Option D */}
                  <ListItem sx={{ bgcolor: selectedQuestion?.correctAnswer === 'D' ? '#e8f5e9' : 'transparent', borderLeft: selectedQuestion?.correctAnswer === 'D' ? '4px solid #4caf50' : 'none' }}>
                    <ListItemText primary={<Typography fontWeight={selectedQuestion?.correctAnswer === 'D' ? 'bold' : 'normal'}>D. {renderInline(selectedQuestion?.optionD, math)}</Typography>} />
                  </ListItem>
                </List>
              </>
            );
          })()}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default TestDetailedResults;
